import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { UserType } from '@models/entities/users'
import { user_list_mock, user_mock } from '@/dev/user_mock'
import type { AxiosError } from 'axios'

import { AxiosErrorHandler } from '@models/classes/axiosErrorHandler'
import { handleApiError } from '@scripts/modules/handleApiError'
import {
  type LoginRequestPayloadDataType,
  type PostUserInfoRequestPayloadDataType
} from '@models/requests/payloads'
import {
  type PermissionsType,
  type SalePointPermissionsType
} from '@youandco/definitions/detailsModels'
import {
  USER_EP,
  USER_LIST_EP,
  USER_LOGIN_EP,
  USER_LOGOUT_EP
} from '@youandco/youandco.endpoints'
import api from '@/axios/api'

const IS_DEV = import.meta.env.MODE === 'development'

export const useUserStore = defineStore('useUserStore', () => {
  const user = ref<UserType | null>(null)
  const userList = ref<UserType[]>([])
  const userListSearch = ref('')

  const isAuthorized = ref(false)
  const isCheckedAuth = ref(false)
  const routeNameForAuthorizedStatusSwitching = ref<string | null>(null)
  const loading = ref(true)

  const isOwnerUser = computed(() => !!user.value?.company)
  const getUser2FAStatus = computed(() => user.value?.login_mfa ?? false)
  const getUserLanguage = computed(() => user.value?.lang)

  const hasPermission = (key: keyof PermissionsType) => computed(() => {
    if (!user.value) return false
    return isOwnerUser.value || !!user.value.user_permissions?.permissions[key]
  })

  const creatingInvoicesAndPayoutsPermission = hasPermission('creating_invoices_and_payouts')
  const viewAnalyticsPermission = hasPermission('view_analytics')
  const viewFinancePermission = hasPermission('view_finance')
  const registryExportPermission = hasPermission('registry_export')
  const manageApiSettingsPermission = hasPermission('manage_api_settings')
  const addingEmployeesPermission = computed(() => !!user.value && isOwnerUser.value)

  const filteredUserList = computed(() =>
    userList.value.filter((u: UserType) =>
      u.email.toLowerCase().includes(userListSearch.value.toLowerCase())))

  const setLoading = (status: boolean) => {
    loading.value = status
  }

  const setUserListSearch = (val: string) => {
    userListSearch.value = val
  }

  const setAuthorizationStatus = (status: boolean, routeName?: string | null) => {
    routeNameForAuthorizedStatusSwitching.value = routeName || (status ? null : 'login')
    isAuthorized.value = status
  }

  const setCheckedAuthorizationStatus = (status: boolean) => {
    isCheckedAuth.value = status
  }

  const getUserInfo = async (isInitial: boolean = false) => {
    if (IS_DEV) {
      user.value = user_mock
      setAuthorizationStatus(true)
      setCheckedAuthorizationStatus(true)
      return
    }

    try {
      const response = await api.get(USER_EP)
      if (response.data) {
        user.value = response.data
      }
      if (isInitial) {
        setAuthorizationStatus(true)
        setCheckedAuthorizationStatus(true)
      }
    } catch (error) {
      handleApiError(error)
      if (isInitial) {
        setCheckedAuthorizationStatus(true)
      }
    }
  }

  const getUserList = async () => {
    if (IS_DEV) {
      userList.value = user_list_mock
      return
    }

    try {
      const response = await api.get(USER_LIST_EP)
      if (response.data) {
        userList.value = response.data
      }
    } catch (error) {
      handleApiError(error)
    }
  }

  const updateUserListEl = (
    id: number,
    salepoints: SalePointPermissionsType[],
    permissions: PermissionsType
  ) => {
    const listUser = userList.value.find((u) => u.id === id)
    if (!listUser) return
    Object.assign(listUser, {
      user_permissions: { salepoints, permissions }
    })
  }

  const postUserInfo = async (payloadData: PostUserInfoRequestPayloadDataType) => {
    try {
      await api.post(USER_EP, payloadData)
    } catch (error) {
      handleApiError(error)
    }
  }

  const login = async (payloadData: LoginRequestPayloadDataType) => {
    return await api.post(USER_LOGIN_EP, payloadData)
  }

  const logOut = async (successRouteName: string = 'login', reload: boolean = true) => {
    try {
      await api.post(USER_LOGOUT_EP)
      setAuthorizationStatus(false, successRouteName)
    } catch (e) {
      new AxiosErrorHandler(e as AxiosError)
    } finally {
      if (reload) { window.location.reload() }
    }
  }

  return {
    user,
    userList,
    isAuthorized,
    isCheckedAuth,
    routeNameForAuthorizedStatusSwitching,
    getUserLanguage,
    getUser2FAStatus,
    filteredUserList,
    userListSearch,
    isOwnerUser,
    creatingInvoicesAndPayoutsPermission,
    viewAnalyticsPermission,
    viewFinancePermission,
    registryExportPermission,
    manageApiSettingsPermission,
    addingEmployeesPermission,
    loading,
    setLoading,
    setAuthorizationStatus,
    setCheckedAuthorizationStatus,
    getUserInfo,
    getUserList,
    postUserInfo,
    setUserListSearch,
    login,
    logOut,
    updateUserListEl
  }
})
